import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import { type CostObjectTag, projectTagFields, refineProjectTag } from './cost-objects.js'
import type { ExpenseSupplierRef } from './finance.js'

// ── recurring expenses (ADR-043) ──────────────────────────────────────────────

export const RECURRING_FREQUENCIES = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'] as const
export type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number]

/** ACTIVE generates; PAUSED waits (resuming skips the dates that passed); ENDED is final. */
export const RECURRING_STATUSES = ['ACTIVE', 'PAUSED', 'ENDED'] as const
export type RecurringStatus = (typeof RECURRING_STATUSES)[number]

/** PENDING waits for review; POSTED created its expense; SKIPPED was dismissed by a user. */
export const OCCURRENCE_STATUSES = ['PENDING', 'POSTED', 'SKIPPED'] as const
export type OccurrenceStatus = (typeof OCCURRENCE_STATUSES)[number]

/** The day of month that stands for "the last day of every month" (shorter months clamp to their end). */
export const LAST_DAY_OF_MONTH = 31

/** How many past dates one run creates at most; a longer backlog continues on the next daily run. */
export const RECURRING_CATCH_UP_LIMIT = 60

/**
 * When a recurring expense falls. Monthly kinds fall on `dayOfMonth` (31 = the month's last day, any
 * day past a month's end clamps to it) every `interval` × 1, 3 or 12 months counted from the start
 * date's month; weekly ones every `interval` weeks from the start date. Dates before the start, after
 * `endDate` or beyond `maxOccurrences` never occur.
 */
export interface RecurringSchedule {
  frequency: RecurringFrequency
  interval: number
  /** Monthly kinds only; null for weekly. */
  dayOfMonth: number | null
  startDate: string
  endDate: string | null
  maxOccurrences: number | null
}

const MONTHS_PER_STEP: Record<Exclude<RecurringFrequency, 'WEEKLY'>, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  YEARLY: 12,
}
const DAY_MS = 86_400_000

function dayNumber(iso: string): number {
  return Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) / DAY_MS
}

function isoOfDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10)
}

function monthIndex(iso: string): number {
  return Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1
}

/** The date in the month `index` (year × 12 + month − 1) on `day`, clamped to the month's last day. */
function dateInMonth(index: number, day: number): string {
  const year = Math.floor(index / 12)
  const month = index % 12
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  return isoOfDay(Date.UTC(year, month, Math.min(day, last)) / DAY_MS)
}

/**
 * The first date of the schedule strictly after `after` (the first date at all when null), ignoring
 * `maxOccurrences` (the caller counts what was created); null once past `endDate`.
 */
export function occurrenceAfter(s: RecurringSchedule, after: string | null): string | null {
  const from = after !== null && after >= s.startDate ? after : null
  let next: string
  if (s.frequency === 'WEEKLY') {
    const step = 7 * s.interval
    const start = dayNumber(s.startDate)
    next =
      from === null
        ? s.startDate
        : isoOfDay(start + (Math.floor((dayNumber(from) - start) / step) + 1) * step)
  } else {
    const step = MONTHS_PER_STEP[s.frequency] * s.interval
    const start = monthIndex(s.startDate)
    const day = s.dayOfMonth ?? Number(s.startDate.slice(8, 10))
    // The step at or just before `from`, then forward to the first date that qualifies (at most three).
    let k = from === null ? 0 : Math.max(0, Math.floor((monthIndex(from) - start) / step))
    for (;;) {
      const candidate = dateInMonth(start + k * step, day)
      if (candidate >= s.startDate && (from === null || candidate > from)) {
        next = candidate
        break
      }
      k++
    }
  }
  return s.endDate !== null && next > s.endDate ? null : next
}

/**
 * Up to `count` dates after `after`, given that `created` occurrences already exist (for
 * `maxOccurrences`): what the form previews and the detail page lists as "coming up".
 */
export function upcomingOccurrences(
  s: RecurringSchedule,
  after: string | null,
  count: number,
  created = 0,
): string[] {
  const out: string[] = []
  let cursor = after
  while (out.length < count) {
    if (s.maxOccurrences !== null && created + out.length >= s.maxOccurrences) break
    const next = occurrenceAfter(s, cursor)
    if (next === null) break
    out.push(next)
    cursor = next
  }
  return out
}

/** How many dates from the first through `today` (inclusive), capped at `limit`: what saving creates at once. */
export function dueOccurrenceCount(
  s: RecurringSchedule,
  today: string,
  limit = RECURRING_CATCH_UP_LIMIT,
): number {
  let n = 0
  let cursor: string | null = null
  while (n < limit) {
    if (s.maxOccurrences !== null && n >= s.maxOccurrences) break
    const next = occurrenceAfter(s, cursor)
    if (next === null || next > today) break
    n++
    cursor = next
  }
  return n
}

const recurringExpenseFields = z.object({
  /** What it is, for lists: "Office rent". */
  name: z.string().trim().min(1, 'Name is required').max(100),
  categoryId: z.uuid(),
  /** Overrides the category's default account (expense or asset account). */
  accountId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  treasuryAccountId: z.uuid(),
  payee: optionalText(200),
  /** The supplier each expense it creates names (G17); copied to them. */
  supplierId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  reference: optionalText(100),
  description: z.string().trim().min(1, 'Describe the expense').max(500),
  /** Net of tax. */
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  taxRateId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  ...projectTagFields,
  frequency: z.enum(RECURRING_FREQUENCIES).default('MONTHLY'),
  /** Every `interval` weeks, months, quarters or years. */
  interval: z.number().int().min(1).max(12).default(1),
  /** Monthly kinds: 1–31 (31 = the last day); defaults to the start date's day. Ignored for weekly. */
  dayOfMonth: z
    .number()
    .int()
    .min(1)
    .max(31)
    .nullish()
    .transform((v) => v ?? null),
  /** The first date it may fall on. */
  startDate: z.iso.date(),
  endDate: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
  maxOccurrences: z
    .number()
    .int()
    .min(1)
    .max(999)
    .nullish()
    .transform((v) => v ?? null),
  /** true: each date posts its expense; false (default): each date waits for review. */
  autoPost: z.boolean().default(false),
})

type Fields = z.output<typeof recurringExpenseFields>

function refineSchedule(v: Fields, ctx: z.RefinementCtx): void {
  if (v.endDate !== null && v.endDate < v.startDate) {
    ctx.addIssue({
      code: 'custom',
      path: ['endDate'],
      message: 'The end date cannot be before the start date',
    })
  }
  if (v.endDate !== null && v.maxOccurrences !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['maxOccurrences'],
      message: 'Choose an end date or a number of times, not both',
    })
  }
}

/** Weekly schedules carry no day of month; monthly ones default to the start date's day. */
function normaliseSchedule<T extends Fields>(v: T): T {
  return {
    ...v,
    dayOfMonth: v.frequency === 'WEEKLY' ? null : (v.dayOfMonth ?? Number(v.startDate.slice(8, 10))),
  }
}

export const recurringExpenseInputSchema = recurringExpenseFields
  .extend({
    /**
     * A posted expense that is this schedule's first date ("Make this recurring", "Repeat monthly"):
     * linked as its first occurrence. The schedule's first date must be the expense's date.
     */
    sourceExpenseId: z.uuid().optional(),
  })
  .superRefine(refineProjectTag)
  .superRefine(refineSchedule)
  .transform(normaliseSchedule)
export type RecurringExpenseInput = z.input<typeof recurringExpenseInputSchema>

export const recurringExpenseUpdateSchema = recurringExpenseFields
  .extend({ version: versionSchema })
  .superRefine(refineProjectTag)
  .superRefine(refineSchedule)
  .transform(normaliseSchedule)
export type RecurringExpenseUpdate = z.input<typeof recurringExpenseUpdateSchema>

export const RECURRING_EXPENSE_SORT_FIELDS = ['name', 'nextRunDate', 'amount'] as const

export const recurringExpenseListQuerySchema = pageQuerySchema.extend({
  status: z.enum(RECURRING_STATUSES).optional(),
})
export type RecurringExpenseListQuery = z.input<typeof recurringExpenseListQuerySchema>

/**
 * Posting a pending occurrence: the template's values unless overridden here (a utility bill's amount
 * differs each month). The date defaults to the occurrence's own.
 */
export const recurringOccurrencePostSchema = z.object({
  expenseDate: z.iso.date().optional(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero').optional(),
  reference: optionalText(100).optional(),
  description: z.string().trim().min(1, 'Describe the expense').max(500).optional(),
})
export type RecurringOccurrencePost = z.input<typeof recurringOccurrencePostSchema>

export interface RecurringExpenseListItem {
  id: string
  name: string
  category: { id: string; name: string; nameAr: string | null }
  treasuryAccount: { id: string; name: string }
  payee: string | null
  description: string
  amount: string
  /** At the tax rate's current percentage; each expense takes the rate in force when it is created. */
  taxAmount: string
  total: string
  frequency: RecurringFrequency
  interval: number
  dayOfMonth: number | null
  startDate: string
  endDate: string | null
  maxOccurrences: number | null
  autoPost: boolean
  status: RecurringStatus
  /** The next date it falls on; null once ended. */
  nextRunDate: string | null
  /** Occurrences waiting for review. */
  pendingCount: number
  /** Why the last run could not create an expense (it retries daily); null when it worked. */
  lastError: { code: string; message: string; at: string } | null
}

export interface RecurringExpenseOccurrence {
  id: string
  occurrenceDate: string
  status: OccurrenceStatus
  expense: { id: string; number: string; total: string; status: 'POSTED' | 'VOID' } | null
  resolvedAt: string | null
}

export interface RecurringExpense extends RecurringExpenseListItem {
  /** The supplier its expenses name (G17); null when none. Optional in the type only for older fixtures. */
  supplier?: ExpenseSupplierRef | null
  /** The account override; null posts to the category's default. */
  account: { id: string; code: string; name: string } | null
  /** What its expenses post to: the override, else the category's default account. */
  postsTo: { id: string; code: string; name: string }
  reference: string | null
  taxRateId: string | null
  /** The tax rate's current percentage (0 without one). */
  taxRate: string
  projectTag: CostObjectTag | null
  /** Occurrences so far (posted, pending and skipped), for `maxOccurrences`. */
  occurrenceCount: number
  /** The latest 50, newest first. */
  occurrences: RecurringExpenseOccurrence[]
  version: number
  createdAt: string
  updatedAt: string
}

/** An occurrence waiting for review, across templates: `GET /recurring-expenses/pending`. */
export interface PendingRecurringOccurrence {
  id: string
  occurrenceDate: string
  recurringExpense: {
    id: string
    name: string
    payee: string | null
    reference: string | null
    description: string
    amount: string
    total: string
    treasuryAccount: { id: string; name: string }
  }
}
